import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import DateRangePicker from '@/components/molecules/DateRangePicker/DateRangePicker';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { ExportDataDropDown } from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import { useAuth, usePermissions } from '@/contexts/auth';
import { useCage, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { isValidCageId } from '@/domain/cages';
import { speciesTraits } from '@/domain/cages/speciesTraits';
import { useGetHealthOverview } from '@/domain/health';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { useGetWelfareImages } from '@/domain/images';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { parseLocale } from '@/utils/localeFormatters';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '../../components/healthDetailChartConstants';
import {
    buildHealthOverviewDownloadData,
    createHealthOverviewExportLabelContext,
} from '../../HealthDashboard/healthOverviewDownloadData';
import { mapHealthOverviewToWoundsInsightDisplay } from '../_utils/healthWoundsInsightDisplay';
import HealthWoundsBodySubTabBar from '../components/HealthWoundsBodySubTabBar';
import HealthWoundsPrimaryTabBar from '../components/HealthWoundsPrimaryTabBar';

import HealthWoundsBodyInsightView from './HealthWoundsBodyInsightView';

export default function HealthWoundsBodyInsightContainer() {
    const { t, i18n } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();
    const { user } = useAuth();
    const { isOptoScaleAdmin } = usePermissions();
    const { cageId, stock, loading: cageLoading } = useCage();
    const timezone = useCageTimezone();
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const locale = parseLocale(i18n.language);

    // WB-1252: a date range and an export, for the clients the flag is released to.
    const { enabled: hasDateRange } = usePostHogFeatureFlag('wound-insight-date-range');

    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'wounds-insight',
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
        enabled: hasDateRange,
    });

    const overviewQueryParams = useMemo(
        () =>
            hasDateRange
                ? {
                      daypartTag,
                      from: dateFrom,
                      to: dateTo,
                      fishGroupId: selectedFishGroupId,
                  }
                : { daypartTag, fishGroupId: selectedFishGroupId },
        [daypartTag, dateFrom, dateTo, hasDateRange, selectedFishGroupId]
    );

    const {
        data: overview,
        isLoading: overviewLoading,
        isFetching: overviewFetching,
    } = useGetHealthOverview(cageId, overviewQueryParams, {
        enabled: isValidCageId(cageId) && (!hasDateRange || Boolean(dateFrom && dateTo)),
        staleTime: 5 * 60 * 1000,
    });

    const imagesFishGroup = usePenCohortFishGroup();
    const { data: imagesResponse, isLoading: imagesLoading } = useGetWelfareImages(
        cageId,
        { type: MERGED_WELFARE_IMAGES_TYPE },
        { emptyWhileLoading: true, keepPreviousData: true, fishGroup: imagesFishGroup }
    );

    const onCommitFalseDetection = useCommitImageFeedback({ cageId });

    // Withheld until the pen's species is known, so a trout pen never shows it while the cage
    // context resolves. A species that opts out here stays out when its dashboard model improves.
    const showDowngradeEstimate =
        !cageLoading && speciesTraits(stock?.fishType?.shortName).woundInsightDowngradeEstimate;

    const isLoading = overviewLoading || overviewFetching || imagesLoading;
    const insight = useMemo(() => mapHealthOverviewToWoundsInsightDisplay(overview), [overview]);

    const woundDailyTrend = overview?.charts.woundDailyTrend ?? [];

    const exportPayload = useMemo(() => {
        if (!hasDateRange || woundDailyTrend.length === 0) {
            return null;
        }

        const labels = createHealthOverviewExportLabelContext(t, tGlobal, (key) => key);
        return buildHealthOverviewDownloadData([], woundDailyTrend, labels);
    }, [hasDateRange, woundDailyTrend, t, tGlobal]);

    return (
        <PageLayout title={t('routes.wounds')} subtitle={t('routes.health')}>
            <HealthWoundsPrimaryTabBar />
            <HealthWoundsBodySubTabBar
                trailing={
                    hasDateRange ? (
                        <>
                            <DateRangePicker
                                disabled={isLoading}
                                menuPosition="right"
                                defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                onDateChange={onDateChange}
                            />
                            {exportPayload ? (
                                <ExportDataDropDown>
                                    <DownloadOptions
                                        data={exportPayload.data}
                                        columns={exportPayload.columns}
                                        nameToInclude="WoundInsight"
                                    />
                                </ExportDataDropDown>
                            ) : null}
                        </>
                    ) : undefined
                }
            />
            <HealthWoundsBodyInsightView
                isLoading={isLoading}
                insight={insight}
                showDowngradeEstimate={showDowngradeEstimate}
                imagesData={imagesResponse ?? []}
                cageId={cageId}
                timezone={timezone ?? 'UTC'}
                isOptoScaleAdmin={isOptoScaleAdmin}
                onCommitFalseDetection={onCommitFalseDetection}
                locale={locale}
            />
        </PageLayout>
    );
}
